import { Injectable } from '@angular/core';
import { Dealer } from '@app/_models/dealer';
import { MakeModel } from '@app/_models/makeModel';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { AlertService } from '@app/_services/alert.service';
import { ConfigDataService } from '@app/_services/config-data.service';
import * as XLSX from 'xlsx/xlsx.mjs';
import { Certificate } from '@app/_models/certificate';
import { PlanDetails } from '@app/_models/planDetails';
import { StateCity } from '@app/_models/stateCity';

/**
 * Enhanced Excel Bulk Individual Template Service
 * 
 * Provides robust Excel template processing with improved:
 * - Date of birth validation and parsing
 * - Comprehensive field validation
 * - Enhanced error reporting
 * - Better data integrity checks
 */
@Injectable({
  providedIn: 'root'
})
export class ExcelBulkIndvTemplateService {
  // Template and processing configuration
  modelNames: string[] = [];
  fileName: string;
  excelData: any[] = [];
  dealer: Dealer;
  errorString: string;
  error = false;
  heroDsLTemplate = false;

  // Validation patterns
  private readonly phoneTestRegexP = new RegExp(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im);
  private readonly datePattern = /^\d{2}-\d{2}-\d{4}$/;
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Data collections
  selectedMakeModel: MakeModel[] = [];
  certificates: Certificate[] = [];
  makeList: string[] = [];
  stateList: string[] = [];
  cityList: string[] = [];
  possibleModels: MakeModel[] = [];
  stateCityList: StateCity[] = [];

  // Template detection properties
  detectedTemplateType: string = '';
  detectedBrandType: string = '';

  // Template column definitions for auto-detection
  private readonly templateColumnMappings = {
    alliance: {
      standard: ['CustomerName', 'CustomerPhoneNumber', 'CustomerEmailId', 'ExShwoRoomPrice', 'RegistrationNumber'],
      age: ['CustomerName', 'CustomerPhoneNumber', 'CustomerEmailId', 'ExShwoRoomPrice', 'RegistrationNumber', 'DOB']
    },
    bajaj: {
      standard: ['CustomerName', 'CustomerPhoneNumber', 'CustomerEmailId', 'ExShwoRoomPrice', 'RegistrationNumber', 'Bajaj'],
      age: ['CustomerName', 'CustomerPhoneNumber', 'CustomerEmailId', 'ExShwoRoomPrice', 'RegistrationNumber', 'DOB', 'Bajaj']
    },
    honda: {
      standard: ['CustomerName', 'CustomerPhoneNumber', 'CustomerEmailId', 'ExShwoRoomPrice', 'RegistrationNumber', 'Honda'],
      age: ['CustomerName', 'CustomerPhoneNumber', 'CustomerEmailId', 'ExShwoRoomPrice', 'RegistrationNumber', 'DOB', 'Honda']
    },
    hero: {
      standard: ['DSL', 'VehicleChassisNumber', 'CustomerName', 'CustomerPhoneNumber'],
      age: ['DSL', 'VehicleChassisNumber', 'CustomerName', 'CustomerPhoneNumber', 'DOB']
    },
    heroEnsure: {
      standard: ['Policy_No', 'Make', 'Model', 'ChassisNo', 'EngineNo', 'FirstName', 'MobileNo', 'InsuredGender'],
      age: ['Policy_No', 'Make', 'Model', 'ChassisNo', 'EngineNo', 'FirstName', 'MobileNo', 'InsuredGender', 'InsuredDOB']
    }
  };

  constructor(
    private readonly alertService: AlertService,
    private readonly configDataService: ConfigDataService,
    private readonly http: HttpClient
  ) { }

  /**
   * Download Excel template file for bulk certificate upload
   */
  downloadTemplateFile(dealer: Dealer): void {
    this.fileName = `${dealer.dealerId}.xlsx`;
    
    this.http.get(`${environment.apiUrl}/certificate/getExcelTemplate`, { 
      responseType: 'blob' 
    }).subscribe({
      next: (blob) => {
        try {
          XLSX.writeFile(blob, this.fileName);
        } catch (error) {
          console.error('Error downloading template:', error);
          this.alertService.error('Error downloading template file. Please try again.');
        }
      },
      error: (error) => {
        console.error('Template download error:', error);
        this.alertService.error('Error downloading template. Please contact support.');
      }
    });
  }

  /**
   * Analyze the file structure to determine the best reading approach
   */
  private analyzeFileStructure(workbook: XLSX.WorkBook): any {
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    
    // Try different reading approaches to understand the file structure
    const approaches = [
      { name: 'header1', data: XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false }) },
      { name: 'header2', data: XLSX.utils.sheet_to_json(worksheet, { header: 2, raw: false }) },
      { name: 'noHeader', data: XLSX.utils.sheet_to_json(worksheet, { header: 0, raw: false }) }
    ];
    
    console.log('Testing different reading approaches:');
    approaches.forEach(approach => {
      console.log(`${approach.name}:`, approach.data[0]);
      console.log(`${approach.name} type:`, typeof approach.data[0], Array.isArray(approach.data[0]) ? 'array' : 'object');
    });
    
    // Find the best approach by analyzing the data
    for (const approach of approaches) {
      if (approach.data && approach.data.length > 0) {
        const firstRow = approach.data[0];
        
        // Check if this looks like a header row (contains text, not just numbers)
        if (this.looksLikeHeaderRow(firstRow)) {
          console.log(`Found header row using approach: ${approach.name}`);
          return {
            approach: approach.name,
            headerRow: firstRow,
            data: approach.data,
            isArrayFormat: Array.isArray(firstRow)
          };
        }
      }
    }
    
    // Default to header: 1 approach
    const defaultData = approaches[0].data;
    return {
      approach: 'header1',
      headerRow: defaultData[0],
      data: defaultData,
      isArrayFormat: Array.isArray(defaultData[0])
    };
  }

  /**
   * Process data based on the analyzed file structure
   */
  private processDataFromStructure(fileStructure: any): any[] {
    try {
      if (!fileStructure || !fileStructure.data) {
        console.warn('No data found in file structure');
        return [];
      }

      const { data, headerRow, isArrayFormat, approach } = fileStructure;
      
      if (isArrayFormat) {
        // Data is in array format, convert to object format using header row
        const headers = headerRow;
        return data.slice(1).map((row: any[]) => {
          const obj: any = {};
          headers.forEach((header: string, index: number) => {
            obj[header] = row[index];
          });
          return obj;
        });
      } else {
        // Data is already in object format
        return data;
      }
    } catch (error) {
      console.error('Error processing data from structure:', error);
      return [];
    }
  }

  /**
   * Check if a row looks like a header row
   */
  private looksLikeHeaderRow(row: any): boolean {
    if (!row) return false;
    
    // If it's an array, check if it contains text headers
    if (Array.isArray(row)) {
      const textColumns = row.filter(cell => 
        cell && 
        typeof cell === 'string' && 
        !isNaN(Number(cell)) === false && // Not just a number
        cell.trim().length > 0
      );
      return textColumns.length >= 5; // At least 5 text columns
    }
    
    // If it's an object, check if keys look like column names
    if (typeof row === 'object') {
      const keys = Object.keys(row);
      const textKeys = keys.filter(key => 
        key && 
        typeof key === 'string' && 
        !isNaN(Number(key)) === false && // Not just a number
        key.trim().length > 0
      );
      return textKeys.length >= 5; // At least 5 text column names
    }
    
    return false;
  }

  /**
   * Detect template type based on file structure analysis
   */
  private detectTemplateTypeFromStructure(fileStructure: any): { templateType: string, brandType: string } {
    try {
      if (!fileStructure || !fileStructure.headerRow) {
        console.warn('No header row found in file structure');
        return { templateType: 'alliance', brandType: 'standard' };
      }

      let columnNames: string[] = [];
      
      if (fileStructure.isArrayFormat) {
        // Header row is an array
        columnNames = fileStructure.headerRow.map((col: any) => String(col).trim()).filter((col: string) => col.length > 0);
      } else {
        // Header row is an object
        columnNames = Object.keys(fileStructure.headerRow).map(col => String(col).trim()).filter(col => col.length > 0);
      }
      
      console.log('Detected columns from structure:', columnNames);
      console.log('Looking for Policy_No column:', columnNames.some(col => col.toLowerCase().includes('policy_no')));
      console.log('Looking for ChassisNo column:', columnNames.some(col => col.toLowerCase().includes('chassisno')));
      console.log('Looking for InsuredGender column:', columnNames.some(col => col.toLowerCase().includes('insuredgender')));

      // Special check for Hero Ensure templates using multiple key columns
      const hasPolicyNo = columnNames.some(col => col.toLowerCase().includes('policy_no') || col.toLowerCase().includes('policyno'));
      const hasChassisNo = columnNames.some(col => col.toLowerCase().includes('chassisno') || col.toLowerCase().includes('chassis_no'));
      const hasInsuredGender = columnNames.some(col => col.toLowerCase().includes('insuredgender') || col.toLowerCase().includes('insured_gender'));
      
      if (hasPolicyNo && hasChassisNo && hasInsuredGender) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('insureddob') || col.toLowerCase().includes('insured_dob') || col.toLowerCase().includes('insureddob'));
        console.log('Hero Ensure template detected with columns:', { hasPolicyNo, hasChassisNo, hasInsuredGender, hasAge });
        return { templateType: 'heroEnsure', brandType: hasAge ? 'age' : 'standard' };
      }

      // Special check for Hero DSL templates using DSL column
      if (columnNames.some(col => col.toLowerCase().includes('dsl'))) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('dob') || col.toLowerCase().includes('birth'));
        return { templateType: 'hero', brandType: hasAge ? 'age' : 'standard' };
      }

      // Check for brand-specific indicators
      const lowerColumnNames = columnNames.map(col => col.toLowerCase());
      
      if (lowerColumnNames.some(col => col.includes('bajaj'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'bajaj', brandType: hasAge ? 'age' : 'standard' };
      }
      
      if (lowerColumnNames.some(col => col.includes('honda'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'honda', brandType: hasAge ? 'age' : 'standard' };
      }

      // Default to Alliance template
      const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
      return { templateType: 'alliance', brandType: hasAge ? 'age' : 'standard' };

    } catch (error) {
      console.error('Error detecting template type from structure:', error);
      return { templateType: 'alliance', brandType: 'standard' };
    }
  }

  /**
   * Detect template type based on header row array
   */
  private detectTemplateTypeFromHeaderRow(headerRow: any[]): { templateType: string, brandType: string } {
    try {
      if (!headerRow || !Array.isArray(headerRow)) {
        console.warn('No header row found for template detection');
        return { templateType: 'alliance', brandType: 'standard' };
      }

      // Get all column names from the header row array
      const columnNames = headerRow.map(col => String(col).trim()).filter(col => col.length > 0);
      
      console.log('Detected columns from header row:', columnNames);
      console.log('Looking for Policy_No column:', columnNames.some(col => col.toLowerCase().includes('policy_no')));
      console.log('Looking for ChassisNo column:', columnNames.some(col => col.toLowerCase().includes('chassisno')));
      console.log('Looking for InsuredGender column:', columnNames.some(col => col.toLowerCase().includes('insuredgender')));

      // Special check for Hero Ensure templates using multiple key columns
      const hasPolicyNo = columnNames.some(col => col.toLowerCase().includes('policy_no') || col.toLowerCase().includes('policyno'));
      const hasChassisNo = columnNames.some(col => col.toLowerCase().includes('chassisno') || col.toLowerCase().includes('chassis_no'));
      const hasInsuredGender = columnNames.some(col => col.toLowerCase().includes('insuredgender') || col.toLowerCase().includes('insured_gender'));
      
      if (hasPolicyNo && hasChassisNo && hasInsuredGender) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('insureddob') || col.toLowerCase().includes('insured_dob') || col.toLowerCase().includes('insureddob'));
        console.log('Hero Ensure template detected with columns:', { hasPolicyNo, hasChassisNo, hasInsuredGender, hasAge });
        return { templateType: 'heroEnsure', brandType: hasAge ? 'age' : 'standard' };
      }

      // Special check for Hero DSL templates using DSL column
      if (columnNames.some(col => col.toLowerCase().includes('dsl'))) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('dob') || col.toLowerCase().includes('birth'));
        return { templateType: 'hero', brandType: hasAge ? 'age' : 'standard' };
      }

      // Check for brand-specific indicators
      const lowerColumnNames = columnNames.map(col => col.toLowerCase());
      
      if (lowerColumnNames.some(col => col.includes('bajaj'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'bajaj', brandType: hasAge ? 'age' : 'standard' };
      }
      
      if (lowerColumnNames.some(col => col.includes('honda'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'honda', brandType: hasAge ? 'age' : 'standard' };
      }

      // Default to Alliance template
      const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
      return { templateType: 'alliance', brandType: hasAge ? 'age' : 'standard' };

    } catch (error) {
      console.error('Error detecting template type from header row:', error);
      return { templateType: 'alliance', brandType: 'standard' };
    }
  }

  /**
   * Detect template type based on actual data structure
   */
  private detectTemplateTypeFromData(dataRow: any): { templateType: string, brandType: string } {
    try {
      if (!dataRow) {
        console.warn('No data row found for template detection');
        return { templateType: 'alliance', brandType: 'standard' };
      }

      // Get all column names from the data row
      const columnNames = Object.keys(dataRow).map(col => String(col).trim()).filter(col => col.length > 0);
      
      console.log('Detected columns from data:', columnNames);
      console.log('Looking for Policy_No column:', columnNames.some(col => col.toLowerCase().includes('policy_no')));
      console.log('Looking for ChassisNo column:', columnNames.some(col => col.toLowerCase().includes('chassisno')));
      console.log('Looking for InsuredGender column:', columnNames.some(col => col.toLowerCase().includes('insuredgender')));

      // Special check for Hero Ensure templates using multiple key columns
      const hasPolicyNo = columnNames.some(col => col.toLowerCase().includes('policy_no') || col.toLowerCase().includes('policyno'));
      const hasChassisNo = columnNames.some(col => col.toLowerCase().includes('chassisno') || col.toLowerCase().includes('chassis_no'));
      const hasInsuredGender = columnNames.some(col => col.toLowerCase().includes('insuredgender') || col.toLowerCase().includes('insured_gender'));
      
      if (hasPolicyNo && hasChassisNo && hasInsuredGender) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('insureddob') || col.toLowerCase().includes('insured_dob') || col.toLowerCase().includes('insureddob'));
        console.log('Hero Ensure template detected with columns:', { hasPolicyNo, hasChassisNo, hasInsuredGender, hasAge });
        return { templateType: 'heroEnsure', brandType: hasAge ? 'age' : 'standard' };
      }

      // Special check for Hero DSL templates using DSL column
      if (columnNames.some(col => col.toLowerCase().includes('dsl'))) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('dob') || col.toLowerCase().includes('birth'));
        return { templateType: 'hero', brandType: hasAge ? 'age' : 'standard' };
      }

      // Check for brand-specific indicators
      const lowerColumnNames = columnNames.map(col => col.toLowerCase());
      
      if (lowerColumnNames.some(col => col.includes('bajaj'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'bajaj', brandType: hasAge ? 'age' : 'standard' };
      }
      
      if (lowerColumnNames.some(col => col.includes('honda'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'honda', brandType: hasAge ? 'age' : 'standard' };
      }

      // Default to Alliance template
      const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
      return { templateType: 'alliance', brandType: hasAge ? 'age' : 'standard' };

    } catch (error) {
      console.error('Error detecting template type from data:', error);
      return { templateType: 'alliance', brandType: 'standard' };
    }
  }

  /**
   * Detect template type based on column headers in uploaded Excel file (legacy method)
   */
  private detectTemplateType(workbook: XLSX.WorkBook): { templateType: string, brandType: string } {
    try {
      // Get the first sheet
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // For Hero Ensure template, try to get headers from the first row
      // The template structure might be different
      let headerRow = XLSX.utils.sheet_to_json(worksheet, { 
        range: 1, 
        header: 1,
        defval: ''
      })[0] as any;
      
      // Check if this looks like a data row (starts with numbers) vs header row
      const firstValue = Object.values(headerRow)[0];
      if (firstValue && !isNaN(Number(firstValue))) {
        // This looks like a data row, try to get headers from the actual header row
        // For Hero Ensure template, headers might be in a different format
        const allRows = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: ''
        });
        
        // Look for a row that contains text headers (not numbers)
        for (let i = 0; i < Math.min(3, allRows.length); i++) {
          const row = allRows[i] as any;
          const firstColValue = Object.values(row)[0];
          if (firstColValue && isNaN(Number(firstColValue)) && typeof firstColValue === 'string') {
            headerRow = row;
            break;
          }
        }
      }

      if (!headerRow) {
        console.warn('No header row found in the uploaded file');
        return { templateType: 'alliance', brandType: 'standard' };
      }

      // Get all column names from the header row
      const columnNames = Object.values(headerRow).map(col => String(col).trim()).filter(col => col.length > 0);
      
      console.log('Detected columns:', columnNames);
      console.log('Looking for Policy_No column:', columnNames.some(col => col.toLowerCase().includes('policy_no')));
      console.log('Looking for PolicyNo column:', columnNames.some(col => col.toLowerCase().includes('policyno')));
      console.log('Looking for ChassisNo column:', columnNames.some(col => col.toLowerCase().includes('chassisno')));
      console.log('Looking for InsuredGender column:', columnNames.some(col => col.toLowerCase().includes('insuredgender')));

      // Check for each template type
      for (const [brand, templates] of Object.entries(this.templateColumnMappings)) {
        for (const [type, requiredColumns] of Object.entries(templates)) {
          const matchScore = this.calculateColumnMatchScore(columnNames, requiredColumns);
          
          // If we have a high match score (80% or more of required columns), consider it a match
          if (matchScore >= 0.8) {
            console.log(`Template detected: ${brand} - ${type} (match score: ${matchScore})`);
            return { templateType: brand, brandType: type };
          }
        }
      }

      // Special check for Hero Ensure templates using multiple key columns
      const hasPolicyNo = columnNames.some(col => col.toLowerCase().includes('policy_no') || col.toLowerCase().includes('policyno'));
      const hasChassisNo = columnNames.some(col => col.toLowerCase().includes('chassisno') || col.toLowerCase().includes('chassis_no'));
      const hasInsuredGender = columnNames.some(col => col.toLowerCase().includes('insuredgender') || col.toLowerCase().includes('insured_gender'));
      
      if (hasPolicyNo && hasChassisNo && hasInsuredGender) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('insureddob') || col.toLowerCase().includes('insured_dob') || col.toLowerCase().includes('insureddob'));
        console.log('Hero Ensure template detected with columns:', { hasPolicyNo, hasChassisNo, hasInsuredGender, hasAge });
        return { templateType: 'heroEnsure', brandType: hasAge ? 'age' : 'standard' };
      }

      // Special check for Hero DSL templates using DSL column
      if (columnNames.some(col => col.toLowerCase().includes('dsl'))) {
        const hasAge = columnNames.some(col => col.toLowerCase().includes('dob') || col.toLowerCase().includes('birth'));
        return { templateType: 'hero', brandType: hasAge ? 'age' : 'standard' };
      }

      // Check for brand-specific indicators
      const lowerColumnNames = columnNames.map(col => col.toLowerCase());
      
      if (lowerColumnNames.some(col => col.includes('bajaj'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'bajaj', brandType: hasAge ? 'age' : 'standard' };
      }
      
      if (lowerColumnNames.some(col => col.includes('honda'))) {
        const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
        return { templateType: 'honda', brandType: hasAge ? 'age' : 'standard' };
      }

      // Default to Alliance template
      const hasAge = lowerColumnNames.some(col => col.includes('dob') || col.includes('birth'));
      return { templateType: 'alliance', brandType: hasAge ? 'age' : 'standard' };

    } catch (error) {
      console.error('Error detecting template type:', error);
      return { templateType: 'alliance', brandType: 'standard' };
    }
  }

  /**
   * Calculate match score between uploaded columns and required template columns
   */
  private calculateColumnMatchScore(uploadedColumns: string[], requiredColumns: string[]): number {
    if (requiredColumns.length === 0) return 0;

    const normalizedUploaded = uploadedColumns.map(col => col.toLowerCase().trim());
    const normalizedRequired = requiredColumns.map(col => col.toLowerCase().trim());

    let matchCount = 0;
    for (const required of normalizedRequired) {
      if (normalizedUploaded.some(uploaded => 
        uploaded.includes(required) || required.includes(uploaded) || 
        this.areColumnsSimilar(uploaded, required)
      )) {
        matchCount++;
      }
    }

    return matchCount / requiredColumns.length;
  }

  /**
   * Check if two column names are similar (handles variations in naming)
   */
  private areColumnsSimilar(col1: string, col2: string): boolean {
    const columnVariations = {
      'customername': ['customer_name', 'name', 'customer'],
      'customerphoneno': ['phone', 'phoneno', 'mobile', 'contact'],
      'customeremailid': ['email', 'emailid', 'mail'],
      'exshwroomprice': ['price', 'amount', 'showroom_price'],
      'registrationnumber': ['registration', 'regno', 'reg_no'],
      'dob': ['dateofbirth', 'birth_date', 'age']
    };

    const normalize = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
    const norm1 = normalize(col1);
    const norm2 = normalize(col2);

    // Direct match
    if (norm1 === norm2) return true;

    // Check variations
    for (const [key, variations] of Object.entries(columnVariations)) {
      if ((norm1 === key && variations.includes(norm2)) || 
          (norm2 === key && variations.includes(norm1)) ||
          (variations.includes(norm1) && variations.includes(norm2))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Upload and process Excel file with enhanced validation
   */
  async uploadFile(
    fileData: any, 
    dealer: Dealer, 
    possibleModels: MakeModel[], 
    selectedPlan: PlanDetails,
    stateCityList: StateCity[]
  ): Promise<void> {
    try {
      this.initializeUpload();
      
      if (!this.validateFileInput(fileData)) {
        return;
      }

      const file = fileData.target.files[0];
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      
      if (!workbook.SheetNames.length) {
        throw new Error('No worksheets found in the uploaded file.');
      }

      // Analyze the file structure to determine the best reading approach
      const fileStructure = this.analyzeFileStructure(workbook);
      console.log('File structure analysis:', fileStructure);
      
      // Detect template type based on the file structure
      const templateDetection = this.detectTemplateTypeFromStructure(fileStructure);
      this.detectedTemplateType = templateDetection.templateType;
      this.detectedBrandType = templateDetection.brandType;

      // Log detection result for debugging
      console.log(`Auto-detected template: ${this.detectedTemplateType} - ${this.detectedBrandType}`);
      
      // Show user feedback about detected template
      this.alertService.info(
        `Template auto-detected: ${this.detectedTemplateType.toUpperCase()} (${this.detectedBrandType === 'age' ? 'with Age/DOB fields' : 'Standard'})`
      );

      // Set Hero DSL template flag if detected
      this.heroDsLTemplate = this.detectedTemplateType === 'hero';

      // Process data based on the analyzed file structure
      let rawData = this.processDataFromStructure(fileStructure);
      
      console.log('Raw Excel data (first row):', rawData[0]);
      console.log('Detected template type:', this.detectedTemplateType);
      
      this.excelData = rawData.filter(row => {
        // Filter based on detected template type
        if (this.detectedTemplateType === 'heroEnsure') {
          const hasData = row.FirstName || row.Policy_No || row.Make;
          console.log('Hero Ensure row check:', { FirstName: row.FirstName, Policy_No: row.Policy_No, Make: row.Make, hasData });
          return hasData;
        } else if (this.detectedTemplateType === 'hero') {
          return row.CustomerName || row.DSL; // Hero DSL templates might use different primary fields
        }
        return row.CustomerName;
      });

      if (!this.excelData.length) {
        this.alertService.error('No valid data found in the uploaded file. Please ensure the file contains customer data.');
        this.error = true;
        return;
      }

      this.setupValidationData(dealer, possibleModels, stateCityList);
      this.validateExcelCertificates();
      
    } catch (error) {
      console.error('File upload error:', error);
      this.alertService.error(`Error processing file: ${error.message || 'Please check the file format and try again.'}`);
      this.error = true;
    }
  }

  /**
   * Initialize upload process
   */
  private initializeUpload(): void {
    this.certificates = [];
    this.error = false;
    this.modelNames = [];
  }

  /**
   * Validate file input
   */
  private validateFileInput(fileData: any): boolean {
    if (!fileData?.target?.files?.length) {
      this.alertService.error('Please select a file to upload.');
      this.error = true;
      return false;
    }

    if (fileData.target.files.length !== 1) {
      this.alertService.error('Please select only one file at a time.');
      this.error = true;
      return false;
    }

    const file = fileData.target.files[0];
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExtension)) {
      this.alertService.error('Please upload a valid Excel file (.xlsx or .xls).');
      this.error = true;
      return false;
    }

    return true;
  }

  /**
   * Setup validation data collections
   */
  private setupValidationData(
    dealer: Dealer, 
    possibleModels: MakeModel[], 
    stateCityList: StateCity[]
  ): void {
    this.dealer = dealer;
    this.possibleModels = possibleModels;
    this.makeList = possibleModels.map(model => model.make);
    this.stateCityList = stateCityList;
    
    // Build model names list
    possibleModels.forEach(model => {
      this.modelNames = this.modelNames.concat(model.model);
    });
  }

  /**
   * Validate Excel certificates with enhanced error checking
   */
  private validateExcelCertificates(): void {
    let validCertificateCount = 0;
    let errorCount = 0;

    this.excelData.forEach((row, index) => {
      // Corrected line number calculation to fix off-by-one error
      const lineNumber = index + 2;
      const certificate = new Certificate();
      this.errorString = `<b>Error on Line No ${lineNumber}: </b><ol>`;
      let hasErrors = false;

      try {
        // Handle different template types
        if (this.detectedTemplateType === 'heroEnsure') {
          row = this.convertHeroEnsureTemplate(row);
        } else if (row.CustomerType) {
          this.heroDsLTemplate = true;
          row = this.convertDslTemplate(row);
        } else {
          this.heroDsLTemplate = false;
        }

        row.FirstName = row.CustomerName;

        // Validate all required fields
        hasErrors = this.validateAllFields(row, certificate) || hasErrors;

        if (!hasErrors) {
          this.certificates.push(certificate);
          validCertificateCount++;
        } else {
          this.errorString += "</ol>";
          this.alertService.info(this.errorString, { autoClose: false });
          this.error = true;
          errorCount++;
        }
      } catch (error) {
        console.error(`Error processing row ${lineNumber}:`, error);
        this.alertService.error(`Error processing row ${lineNumber}: ${error.message}`);
        this.error = true;
        errorCount++;
      }
    });

    // Provide summary feedback
    if (validCertificateCount > 0 && errorCount === 0) {
      this.alertService.success(`Successfully validated ${validCertificateCount} certificate(s).`);
    } else if (validCertificateCount > 0 && errorCount > 0) {
      this.alertService.info(`Validated ${validCertificateCount} certificate(s) with ${errorCount} error(s). Please fix errors and re-upload.`);
    } else {
      this.alertService.error('No valid certificates found. Please check the file format and data.');
    }
  }

  /**
   * Validate all certificate fields with comprehensive checking
   */
  private validateAllFields(row: any, certificate: Certificate): boolean {
    let hasErrors = false;

    // Vehicle information validation
    hasErrors = this.validateVehicleInfo(row, certificate) || hasErrors;
    
    // Customer information validation
    hasErrors = this.validateCustomerInfo(row, certificate) || hasErrors;
    
    // Date of birth / age validation (enhanced)
    hasErrors = this.validateDateOfBirthOrAge(row, certificate) || hasErrors;
    
    // Address validation
    hasErrors = this.validateAddressInfo(row, certificate) || hasErrors;
    
    // Nominee validation
    hasErrors = this.validateNomineeInfo(row, certificate) || hasErrors;

    return hasErrors;
  }

  /**
   * Enhanced validation for age or date of birth with multiple column name support
   */
  private validateDateOfBirthOrAge(row: any, certificate: Certificate): boolean {
    let hasErrors = false;

    // Check for Age first
    if (row.Age && row.Age.toString().trim() !== '') {
      const age = parseInt(row.Age);
      if (isNaN(age) || age < 15 || age > 80) {
        this.errorString += `<li>Invalid Age: ${row.Age}. Age should be between 15 to 80 years</li>`;
        hasErrors = true;
      } else {
        certificate.age = age;
        certificate.ageOrDob = "age";
      }
    } else {
      // Check for various date of birth column names
      const dobColumnNames = [
        'DateOfBirth(DD-MM-YYYY)',
        'Date of Birth',
        'DateOfBirth', 
        'DOB',
        'Date_of_Birth',
        'Birth_Date',
        'BirthDate'
      ];
      
      let dobValue = null;
      let foundColumn = null;
      
      for (const columnName of dobColumnNames) {
        if (row[columnName] && row[columnName].toString().trim() !== '') {
          dobValue = row[columnName].toString().trim();
          foundColumn = columnName;
          break;
        }
      }
      
      if (dobValue) {
        // Enhanced DOB validation
        hasErrors = this.validateDateOfBirth(dobValue, certificate) || hasErrors;
      } else {
        this.errorString += "<li>Missing both Age and DateOfBirth. At least one is required. Supported DOB column names: DateOfBirth(DD-MM-YYYY), DOB, Date of Birth, DateOfBirth</li>";
        hasErrors = true;
      }
    }

    return hasErrors;
  }

  /**
   * Enhanced date of birth validation with multiple format support
   */
  private validateDateOfBirth(dobValue: string, certificate: Certificate): boolean {
    if (!dobValue || dobValue.trim() === '') {
      this.errorString += "<li>DateOfBirth(DD-MM-YYYY) is empty</li>";
      return true;
    }

    try {
      // Handle different date formats
      let formattedDob = this.parseDateString(dobValue);
      
      if (!formattedDob) {
        this.errorString += `<li>Invalid DateOfBirth format: ${dobValue}. Expected formats: DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY, or DD/MM/YY (e.g., 01-01-1990, 1/1/90, 01.01.1990)</li>`;
        return true;
      }

      // Validate date is reasonable
      const dobDate = new Date(formattedDob);
      const currentDate = new Date();
      const hundredYearsAgo = new Date(currentDate.getFullYear() - 100, currentDate.getMonth(), currentDate.getDate());
      const fifteenYearsAgo = new Date(currentDate.getFullYear() - 15, currentDate.getMonth(), currentDate.getDate());

      if (dobDate > fifteenYearsAgo) {
        this.errorString += `<li>Date of birth indicates age less than 15 years: ${dobValue}</li>`;
        return true;
      }

      if (dobDate < hundredYearsAgo) {
        this.errorString += `<li>Date of birth indicates age more than 100 years: ${dobValue}</li>`;
        return true;
      }

      certificate.dob = formattedDob;
      certificate.ageOrDob = "dob";
      return false;

    } catch (error) {
      this.errorString += `<li>Error parsing DateOfBirth: ${dobValue}. Please use supported formats: DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY, or DD/MM/YY</li>`;
      return true;
    }
  }

  /**
   * Parse date string with multiple format support including 2-digit years
   */
  private parseDateString(dateStr: string): string | null {
    if (!dateStr) return null;

    // Remove any extra whitespace
    dateStr = dateStr.trim();

    // Try DD-MM-YYYY format first
    if (this.datePattern.test(dateStr)) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        
        if (parseInt(year) > 1900 && parseInt(year) <= new Date().getFullYear()) {
          const result = `${year}-${month}-${day}`;
          return result;
        }
      }
    }

    // Try other common formats including 2-digit years and Hero Ensure format
    const formats = [
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // DD/MM/YYYY or D/M/YYYY
      /^(\d{1,2})-(\d{1,2})-(\d{4})$/,   // DD-MM-YYYY or D-M-YYYY
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/,   // YYYY-MM-DD
      /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,  // DD.MM.YYYY
      /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, // DD/MM/YY or D/M/YY
      /^(\d{1,2})-(\d{1,2})-(\d{2})$/,   // DD-MM-YY or D-M-YY
      /^(\d{1,2})\.(\d{1,2})\.(\d{2})$/,  // DD.MM.YY
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+\d{1,2}:\d{2}:\d{2}\s+[AP]M$/ // Hero Ensure format: M/D/YYYY H:MM:SS AM/PM
    ];

    for (let i = 0; i < formats.length; i++) {
      const format = formats[i];
      const match = dateStr.match(format);
      if (match) {
        let day, month, year;
        
        if (format === formats[2]) { // YYYY-MM-DD
          year = match[1];
          month = match[2];
          day = match[3];
        } else if (format === formats[7]) { // Hero Ensure format: M/D/YYYY H:MM:SS AM/PM
          day = match[1];
          month = match[2];
          year = match[3];
        } else { // DD/MM/YYYY or DD/MM/YY variants
          day = match[1];
          month = match[2];
          year = match[3];
          
          // Handle 2-digit years (formats index 4, 5, 6)
          if (i >= 4 && i <= 6 && year.length === 2) {
            const currentYear = new Date().getFullYear();
            const currentCentury = Math.floor(currentYear / 100) * 100;
            const twoDigitYear = parseInt(year);
            
            // If year is 0-50, assume 2000s, if 51-99, assume 1900s
            if (twoDigitYear <= 50) {
              year = (currentCentury + twoDigitYear).toString();
            } else {
              year = (currentCentury - 100 + twoDigitYear).toString();
            }
          }
        }

        // Pad single digits
        day = day.padStart(2, '0');
        month = month.padStart(2, '0');

        // Validate year range
        const yearInt = parseInt(year);
        if (yearInt > 1900 && yearInt <= new Date().getFullYear()) {
          const result = `${year}-${month}-${day}`;
          return result;
        }
      }
    }

    return null;
  }

  /**
   * Validate vehicle information
   */
  private validateVehicleInfo(row: any, certificate: Certificate): boolean {
    let hasErrors = false;

    // Make validation
    if (row.Make) {
      certificate.vehicleMake = row.Make;
    } else {
      this.errorString += "<li>Missing Make Value</li>";
      hasErrors = true;
    }

    // Model validation
    if (row.Model) {
      certificate.model = row.Model;
    } else {
      this.errorString += "<li>Missing Model Value</li>";
      hasErrors = true;
    }

    // Variant validation
    if (row.Model === "New") {
      if (row.Variant) {
        certificate.variant = row.Variant;
        certificate.model = row.Variant;
      } else {
        this.errorString += "<li>Missing Variant Value when Model selected is 'New'</li>";
        hasErrors = true;
      }
    } else if (row.Variant) {
      certificate.variant = row.Variant;
    }

    // Chassis number validation
    if (row.ChasisNumber) {
      if (this.excelData.filter(a => a.ChasisNumber === row.ChasisNumber).length > 1) {
        this.errorString += "<li>Duplicate ChasisNumber within this sheet</li>";
        hasErrors = true;
      } else {
        certificate.chasisNumber = row.ChasisNumber;
      }
    } else {
      this.errorString += "<li>Missing ChasisNumber Value</li>";
      hasErrors = true;
    }

    // Engine number validation
    if (row.EngineNumber) {
      certificate.engineNumber = row.EngineNumber;
    } else {
      this.errorString += "<li>Missing EngineNumber Value</li>";
      hasErrors = true;
    }

    // Registration number (optional)
    if (row.RegistrationNumber) {
      certificate.registrationNumber = row.RegistrationNumber;
    }

    // Vehicle year validation
    if (row.VehicleManufacturingYear) {
      const currentYear = new Date().getFullYear();
      const validYears = [currentYear, currentYear - 1, currentYear - 2];
      
      if (validYears.includes(parseInt(row.VehicleManufacturingYear))) {
        certificate.vehicleYear = row.VehicleManufacturingYear;
      } else {
        this.errorString += "<li>Invalid vehicle manufacturing year: " + row.VehicleManufacturingYear + ". Valid years: " + validYears.join(', ') + "</li>";
        hasErrors = true;
      }
    } else {
      this.errorString += "<li>Missing VehicleManufacturingYear Value</li>";
      hasErrors = true;
    }

    return hasErrors;
  }

  /**
   * Validate customer information
   */
  private validateCustomerInfo(row: any, certificate: Certificate): boolean {
    let hasErrors = false;

    // First name validation
    if (row.FirstName) {
      certificate.firstName = row.FirstName;
    } else {
      this.errorString += "<li>Missing FirstName Value</li>";
      hasErrors = true;
    }

    // Contact number validation
    if (row.ContactNumber) {
      if (this.phoneTestRegexP.test(row.ContactNumber)) {
        certificate.contactNumber = row.ContactNumber;
      } else {
        this.errorString += "<li>Invalid ContactNumber format</li>";
        hasErrors = true;
      }
    } else {
      this.errorString += "<li>Missing ContactNumber Value</li>";
      hasErrors = true;
    }

    // Email validation (optional but must be valid if provided)
    if (row.Email) {
      if (this.emailPattern.test(row.Email)) {
        certificate.email = row.Email;
      } else {
        this.errorString += "<li>Invalid Email format</li>";
        hasErrors = true;
      }
    }

    // Gender validation
    if (row.Gender) {
      if (['male', 'female'].includes(row.Gender.toLowerCase())) {
        certificate.gender = row.Gender;
      } else {
        this.errorString += "<li>Invalid Gender: " + row.Gender + ". Valid values: Male, Female</li>";
        hasErrors = true;
      }
    } else {
      this.errorString += "<li>Missing Gender Value. Valid values: Male, Female</li>";
      hasErrors = true;
    }

    return hasErrors;
  }

  /**
   * Validate address information
   */
  private validateAddressInfo(row: any, certificate: Certificate): boolean {
    let hasErrors = false;

    // Address validation
    if (row.Address) {
      certificate.address = row.Address;
    } else {
      this.errorString += "<li>Missing Address Value</li>";
      hasErrors = true;
    }

    // State validation
    if (row.State) {
      certificate.state = row.State;
    } else {
      this.errorString += "<li>Missing State Value</li>";
      hasErrors = true;
    }

    // City validation
    if (row.City) {
      certificate.city = row.City;
    } else {
      this.errorString += "<li>Missing City Value</li>";
      hasErrors = true;
    }

    return hasErrors;
  }

  /**
   * Validate nominee information
   */
  private validateNomineeInfo(row: any, certificate: Certificate): boolean {
    let hasErrors = false;

    // Nominee name validation
    if (row.NomineeName) {
      certificate.nomineeName = row.NomineeName;
    } else {
      this.errorString += "<li>Missing NomineeName Value</li>";
      hasErrors = true;
    }

    // Nominee gender validation
    if (row.NomineeGender) {
      if (['male', 'female', 'na'].includes(row.NomineeGender.toLowerCase())) {
        certificate.nomineeGender = row.NomineeGender;
      } else {
        this.errorString += "<li>Invalid NomineeGender: " + row.NomineeGender + ". Valid values: Male, Female, NA</li>";
        hasErrors = true;
      }
    } else {
      this.errorString += "<li>Missing NomineeGender Value</li>";
      hasErrors = true;
    }

    // Nominee relationship validation
    const validRelationships = [
      "spouse", "husband", "wife", "father", "mother", "son", "daughter", 
      "brother", "sister", "grandson", "granddaughter", "other", 
      "uncle", "nephew", "niece"
    ];

    if (row.NomineeRelationship) {
      if (validRelationships.includes(row.NomineeRelationship.toLowerCase())) {
        certificate.nomineeRelationship = row.NomineeRelationship;
      } else {
        this.errorString += "<li>Invalid NomineeRelationship: " + row.NomineeRelationship + ". Valid values: " + validRelationships.join(', ') + "</li>";
        hasErrors = true;
      }
    } else {
      this.errorString += "<li>Missing NomineeRelationship Value</li>";
      hasErrors = true;
    }

    // Nominee age validation
    if (row.NomineeAge) {
      if (!isNaN(row.NomineeAge) || row.NomineeAge === "NA") {
        certificate.nomineeAge = row.NomineeAge;
      } else {
        this.errorString += "<li>Invalid NomineeAge: " + row.NomineeAge + "</li>";
        hasErrors = true;
      }
    } else {
      this.errorString += "<li>Missing NomineeAge Value</li>";
      hasErrors = true;
    }

    return hasErrors;
  }
  /**
   * Convert DSL template format to standard format
   */
  private convertDslTemplate(x: any): any {
    const addressArray = x.Address.split(",");
    x.State = addressArray[addressArray.length - 2]?.trim() || '';
    x.City = addressArray[addressArray.length - 3]?.trim() || '';
    x.Address = addressArray.slice(0, 3).join(',');
    x.VehicleManufacturingYear = new Date().getFullYear().toString();
    x.Make = 'Hero Motocorp Limited';
    x.NomineeName = "Legal Heir";
    x.NomineeGender = "NA";
    x.NomineeRelationship = "Other";
    x.NomineeAge = "NA";
    return x;
  }

  /**
   * Convert Hero Ensure template format to standard format
   */
  private convertHeroEnsureTemplate(x: any): any {
    // Map Hero Ensure template columns to standard format
    x.CustomerName = x.FirstName;
    x.ContactNumber = x.MobileNo;
    // Only set email if EmailID has a value, otherwise leave it undefined/empty
    x.Email = x.EmailID && x.EmailID.trim() ? x.EmailID.trim() : undefined;
    
    // Convert gender format: M -> Male, F -> Female
    if (x.InsuredGender) {
      x.Gender = this.convertGenderFormat(x.InsuredGender);
    }
    
    x.ChasisNumber = x.ChassisNo;
    x.EngineNumber = x.EngineNo;
    x.RegistrationNumber = x.RegistrationNo || 'New';
    x.VehicleManufacturingYear = new Date().getFullYear().toString();
    x.Make = x.Make || 'Hero Motocorp Limited';
    x.Model = x.Model;
    x.Variant = x.Model; // Use model as variant for Hero Ensure template
    
    // Handle address fields
    x.Address = x.Address1 || '';
    if (x.Address2) {
      x.Address += (x.Address ? ', ' : '') + x.Address2;
    }
    if (x.Address3) {
      x.Address += (x.Address ? ', ' : '') + x.Address3;
    }
    
    // Handle nominee information
    x.NomineeName = x.NomineeName || "Legal Heir";
    
    // Convert nominee gender format: M -> Male, F -> Female
    if (x.NomineeGender) {
      x.NomineeGender = this.convertGenderFormat(x.NomineeGender);
    } else {
      x.NomineeGender = "NA";
    }
    
    x.NomineeRelationship = x.NomineeRelationship || "Other";
    x.NomineeAge = x.NomineeAge || "NA";
    
    // Handle DOB/Age fields with proper date conversion
    if (x.InsuredDOB) {
      x.DOB = this.convertDateFormat(x.InsuredDOB);
    } else if (x.InsuredAge) {
      x.Age = x.InsuredAge;
    }
    
    // Handle nominee DOB/Age
    if (x.NomineeDOB) {
      x.NomineeDOB = this.convertDateFormat(x.NomineeDOB);
    } else if (x.NomineeAge) {
      // Keep as is for nominee age
    }
    
    return x;
  }

  /**
   * Convert gender format from M/F to Male/Female
   */
  private convertGenderFormat(gender: string): string {
    if (!gender) return '';
    
    const normalizedGender = gender.toString().trim().toUpperCase();
    switch (normalizedGender) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'MALE':
        return 'Male';
      case 'FEMALE':
        return 'Female';
      default:
        return gender; // Return as-is if not recognized
    }
  }

  /**
   * Convert date format from various formats to DD-MM-YYYY
   */
  private convertDateFormat(dateValue: string): string {
    if (!dateValue) return '';
    
    try {
      // Handle different date formats
      let date: Date;
      
      // Try parsing the date string
      if (typeof dateValue === 'string') {
        // Handle format like "6/20/1995 12:00:00 AM"
        const cleanDate = dateValue.split(' ')[0]; // Remove time part
        date = new Date(cleanDate);
      } else if (dateValue && typeof dateValue === 'object' && 'getTime' in dateValue) {
        date = dateValue as Date;
      } else {
        date = new Date(dateValue);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date format:', dateValue);
        return dateValue; // Return original if can't parse
      }
      
      // Convert to DD-MM-YYYY format
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear().toString();
      
      return `${day}-${month}-${year}`;
      
    } catch (error) {
      console.error('Error converting date format:', dateValue, error);
      return dateValue; // Return original if conversion fails
    }
  }

  /**
   * Get detected template information
   */
  getDetectedTemplate(): { templateType: string, brandType: string, displayName: string } {
    const displayName = this.getTemplateDisplayName(this.detectedTemplateType, this.detectedBrandType);
    return {
      templateType: this.detectedTemplateType,
      brandType: this.detectedBrandType,
      displayName
    };
  }

  /**
   * Get user-friendly display name for template
   */
  private getTemplateDisplayName(templateType: string, brandType: string): string {
    const brandNames = {
      alliance: 'Alliance',
      bajaj: 'Bajaj',
      honda: 'Honda', 
      hero: 'Hero DSL',
      heroEnsure: 'Hero Ensure'
    };

    const typeNames = {
      standard: 'Standard',
      age: 'with Age/DOB fields'
    };

    const brand = brandNames[templateType] || 'Unknown';
    const type = typeNames[brandType] || 'Standard';
    
    return `${brand} Template (${type})`;
  }

  /**
   * Check if template detection is available
   */
  hasDetectedTemplate(): boolean {
    return !!this.detectedTemplateType;
  }
}
